import { DateTime } from 'luxon';

/**
 * The billing cursors (`subscriptionDate` / `nextSubscriptionDate`) are
 * intentionally stamped by the server at UK midnight and 23:59:59 so
 * WHERE clauses on Srts / CoreContacts include the full boundary day.
 * That's fine for aggregation, but it fights us in the UI: parsing
 * `2026-07-14T23:59:59Z` in a browser at UTC+ (e.g. IST) rolls the
 * displayed calendar day forward to Jul 15 and the user sees "15 - 15"
 * instead of "15 - 14".
 *
 * We only care about the UK calendar day for display, so drop the time
 * component from the ISO string and format the resulting plain date.
 * This is stable regardless of browser timezone and doesn't require any
 * timezone plumbing at the call site.
 */
export const formatBillingDate = (
  iso: string | null | undefined,
  format = 'dd LLL, yyyy'
): string => {
  if (!iso) return '';
  const datePart = String(iso).slice(0, 10); // YYYY-MM-DD
  const dt = DateTime.fromISO(datePart);
  if (!dt.isValid) return '';
  return dt.toFormat(format);
};

/**
 * Same idea as `formatBillingDate` but returns a Luxon `DateTime` so
 * callers can chain further transforms (e.g. `.plus`, `.diff`) without
 * inheriting the 23:59:59 time-of-day drift.
 */
export const parseBillingDate = (iso: string | null | undefined): DateTime | null => {
  if (!iso) return null;
  const dt = DateTime.fromISO(String(iso).slice(0, 10));
  return dt.isValid ? dt : null;
};
